import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
  AvatarGroup,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  ContentCopy as DuplicateIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  Analytics as AnalyticsIcon,
  Link as LinkIcon,
  Description as PDFIcon,
  Public as PublicIcon,
  Lock as PrivateIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Stores
import usePDFStore from '../../stores/pdfStore';
import { getPDFUrls } from '../../services/pdfService';

const PDFListItem = ({ pdf, viewMode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { deletePDF, duplicatePDF, generatePDF } = usePDFStore();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Handle menu open
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle edit
  const handleEdit = () => {
    handleMenuClose();
    navigate(`/pdf/edit/${pdf._id}`);
  };
  
  // Handle view
  const handleView = () => {
    const urls = getPDFUrls(pdf);
    window.open(urls.long, '_blank');
  };
  
  // Handle analytics
  const handleAnalytics = () => {
    handleMenuClose();
    navigate(`/pdf/analytics/${pdf._id}`);
  };
  
  // Handle share
  const handleShare = () => {
    handleMenuClose();
    navigate(`/pdf/share/${pdf._id}`);
  };
  
  // Handle duplicate
  const handleDuplicate = async () => {
    handleMenuClose();
    setLoading(true);
    try {
      await duplicatePDF(pdf._id);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle generate
  const handleGenerate = async () => {
    handleMenuClose();
    setLoading(true);
    try {
      await generatePDF(pdf._id);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    handleMenuClose();
    if (window.confirm('Are you sure you want to delete this PDF?')) {
      setLoading(true);
      try {
        await deletePDF(pdf._id);
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Handle copy link
  const handleCopyLink = () => {
    handleMenuClose();
    const urls = getPDFUrls(pdf);
    navigator.clipboard.writeText(urls.short);
    // Show notification
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'published':
        return 'success';
      case 'expired':
        return 'warning';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Get user role badge
  const getUserRoleBadge = () => {
    if (pdf.createdBy?._id === pdf.currentUser?._id) {
      return <Chip label="Owner" size="small" color="primary" />;
    }
    const share = pdf.sharedWith?.find(s => s.userId._id === pdf.currentUser?._id);
    if (share) {
      return <Chip label={share.role} size="small" variant="outlined" />;
    }
    return null;
  };
  
  // List view
  if (viewMode === 'list') {
    return (
      <Card sx={{ mb: 1, '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } }} elevation={0} variant="outlined">
        <CardContent sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 1.5,
          px: 2,
          '&:last-child': { pb: 1.5 }
        }}>
          <PDFIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.25, fontWeight: 500 }}>
              {pdf.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Updated {format(new Date(pdf.updatedAt), 'MMM d, yyyy')}
              </Typography>
              <Chip 
                label={pdf.status} 
                size="small" 
                color={getStatusColor(pdf.status)}
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              {pdf.isPublic && <PublicIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              {pdf.config?.security?.password && <PrivateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Analytics badge */}
            {(pdf.analytics?.totalViews > 0 || pdf.analytics?.downloads > 0) && (
              <Tooltip title={`${pdf.analytics?.totalViews || 0} views, ${pdf.analytics?.downloads || 0} downloads`}>
                <Chip
                  size="small"
                  icon={<AnalyticsIcon sx={{ fontSize: 16 }} />}
                  label={`${pdf.analytics?.totalViews || 0} views`}
                  sx={{ height: 24, fontSize: '0.75rem' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAnalytics();
                  }}
                />
              </Tooltip>
            )}
            
            {getUserRoleBadge()}
            
            <AvatarGroup max={3} sx={{ ml: 1 }}>
              <Tooltip title={`${pdf.createdBy?.firstName} ${pdf.createdBy?.lastName} (Owner)`}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem' }}>
                  {pdf.createdBy?.firstName?.[0]}
                </Avatar>
              </Tooltip>
              {pdf.sharedWith?.map(share => (
                <Tooltip 
                  key={share.userId._id} 
                  title={`${share.userId.firstName} ${share.userId.lastName}`}
                >
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem' }}>
                    {share.userId.firstName?.[0]}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
            
            <Tooltip title="View PDF">
              <IconButton size="small" onClick={handleView} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Analytics">
              <IconButton size="small" onClick={handleAnalytics} sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'primary.main' } }}>
                <AnalyticsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More actions">
              <IconButton size="small" onClick={handleMenuOpen} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <MoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  // Grid view
  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[2]
        }
      }}
      elevation={0}
      variant="outlined"
    >
      <CardContent sx={{ flex: 1, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
          <PDFIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="subtitle2" 
              gutterBottom 
              noWrap 
              fontWeight={500}
              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              onClick={handleView}
            >
              {pdf.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip 
                label={pdf.status} 
                size="small" 
                color={getStatusColor(pdf.status)}
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              {pdf.isPublic && (
                <Chip 
                  icon={<PublicIcon sx={{ fontSize: 14 }} />} 
                  label="Public" 
                  size="small" 
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
              {pdf.config?.security?.password && (
                <Chip 
                  icon={<PrivateIcon sx={{ fontSize: 14 }} />} 
                  label="Protected" 
                  size="small" 
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
              {/* Analytics chip in grid view */}
              {(pdf.analytics?.totalViews > 0 || pdf.analytics?.downloads > 0) && (
                <Chip
                  size="small"
                  icon={<AnalyticsIcon sx={{ fontSize: 14 }} />}
                  label={`${pdf.analytics?.totalViews || 0} views`}
                  sx={{ 
                    height: 20, 
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAnalytics();
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={handleMenuOpen} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
            <MoreIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8125rem' }}>
          {pdf.plainText?.substring(0, 80)}...
        </Typography>
        
        <Box sx={{ mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
            Created by {pdf.createdBy?.firstName} {pdf.createdBy?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {format(new Date(pdf.createdAt), 'MMM d, yyyy')}
          </Typography>
        </Box>
      </CardContent>
      
      <Divider />
      
      <CardActions sx={{ justifyContent: 'space-between', px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {pdf.analytics?.totalViews || 0} views
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {pdf.analytics?.downloads || 0} downloads
          </Typography>
        </Box>
        <Box sx={{ mr: -0.5 }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={handleView} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
              <ViewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Analytics">
            <IconButton size="small" onClick={handleAnalytics} sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'primary.main' } }}>
              <AnalyticsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={handleEdit} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share">
            <IconButton size="small" onClick={handleShare} sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleAnalytics}>
          <ListItemIcon>
            <AnalyticsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Analytics</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Link</ListItemText>
        </MenuItem>
        
        {pdf.isPublic && (
          <MenuItem onClick={() => {
            handleMenuClose();
            const urls = getPDFUrls(pdf);
            const publicUrl = pdf.slug ? urls.slug : urls.short;
            window.open(publicUrl, '_blank');
          }}>
            <ListItemIcon>
              <PublicIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Public Link</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        
        <Divider />
        
        {!pdf.currentPdfUrl && (
          <MenuItem onClick={handleGenerate}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Generate PDF</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default PDFListItem;